import useTranslation from '../useTranslation';
import { useNavigate } from 'react-router-dom';

import { useForm } from '@american-autonomy/ag-ui';

import { PHONE } from 'components/OnboardingStepCount/constants';
import FirstNameField from 'components/fields/FirstNameField';
import LastNameField from 'components/fields/LastNameField';
import PhoneNumberField from 'components/fields/PhoneNumberField';

import useCurrentUser from 'hooks/useCurrentUser';
import useGlobalStore from 'hooks/useGlobalStore';
import usePageRoutes from 'hooks/usePageRoutes';

import {
    COMPLETED,
    FIRST_NAME_FIELD_NAME,
    IN_PROGRESS,
    LAST_NAME_FIELD_NAME,
    PHONE_FIELD_NAME
} from 'pages/OnboardingCurrentUserPage/constants';
import type {
    FieldNames,
    Fields,
    OnboardingUserFormSchema
} from 'pages/OnboardingCurrentUserPage/types';

import type { OnboardingStepId, OnboardingStepItem } from './types';

const useData = () => {
    const {
        FIRST_NAME_BODY_TEXT,
        FIRST_NAME_FOOTER_TEXT,
        FIRST_NAME_PLACEHOLDER,
        LAST_NAME_BODY_TEXT,
        LAST_NAME_FOOTER_TEXT,
        LAST_NAME_PLACEHOLDER,
        PHONE_BODY_TEXT,
        PHONE_FOOTER_TEXT,
        PHONE_PLACEHOLDER
    } = useTranslation();

    const navigate = useNavigate();
    const { onboardingOrganizationCreationPage } = usePageRoutes();
    const { updateCurrentUser } = useCurrentUser();

    const { getValue } = useGlobalStore({
        namespace: 'useCurrentUser'
    });

    const { getValue: getCurrentStepValue, setValue } = useGlobalStore({
        namespace: 'onboardingData'
    });

    const currentUser = getValue('currentUser');
    const currentStepsArray = getCurrentStepValue('currentOnboardingSteps') as
        | OnboardingStepItem[]
        | undefined;

    const currentStep =
        (currentStepsArray?.find(step => step.status === IN_PROGRESS)?.id as FieldNames) ?? PHONE;

    const form = useForm<OnboardingUserFormSchema>({
        defaultValues: currentUser,
        mode: 'onChange'
    });

    const buildSteps = (nextStep: OnboardingStepId) =>
        currentStepsArray?.map(step => {
            if (step.id === currentStep) {
                return { ...step, isValid: true, status: COMPLETED };
            }

            if (step.id === nextStep) {
                return { ...step, status: IN_PROGRESS };
            }

            return step;
        });

    const {
        control,
        formState: { errors },
        handleSubmit,
        setFocus,
        trigger
    } = form;

    const handleOnNext = async (step: FieldNames) => {
        const isValidated = await trigger(currentStep);

        if (isValidated) {
            setValue({
                currentOnboardingSteps: buildSteps(step)
            });

            requestAnimationFrame(() => {
                setFocus(step);
            });
        }
    };

    const handleUpdateUser = async (data: OnboardingUserFormSchema) => {
        const isValidated = await trigger();
        const { firstName, lastName, phone } = data;

        if (isValidated) {
            await updateCurrentUser({
                user: {
                    firstName,
                    lastName,
                    phone
                }
            });
            navigate(onboardingOrganizationCreationPage);
        }
    };

    const fields: Fields = {
        firstName: {
            Component: FirstNameField,
            bodyText: FIRST_NAME_BODY_TEXT,
            footerText: FIRST_NAME_FOOTER_TEXT,
            isRequired: true,
            name: FIRST_NAME_FIELD_NAME,
            onNext: () => handleOnNext('lastName'),
            placeholder: FIRST_NAME_PLACEHOLDER
        },
        lastName: {
            Component: LastNameField,
            bodyText: LAST_NAME_BODY_TEXT,
            footerText: LAST_NAME_FOOTER_TEXT,
            isRequired: true,
            name: LAST_NAME_FIELD_NAME,
            onNext: () => handleOnNext('phone'),
            placeholder: LAST_NAME_PLACEHOLDER
        },
        phone: {
            Component: PhoneNumberField,
            bodyText: PHONE_BODY_TEXT,
            footerText: PHONE_FOOTER_TEXT,
            isRequired: true,
            name: PHONE_FIELD_NAME,
            onNext: handleSubmit(handleUpdateUser),
            placeholder: PHONE_PLACEHOLDER
        }
    };

    return {
        Component: fields[currentStep].Component,
        bodyText: fields[currentStep].bodyText,
        control,
        currentStep,
        errors,
        fields,
        footerText: fields[currentStep].footerText,
        form,
        isRequired: fields[currentStep].isRequired,
        name: fields[currentStep].name,
        onNext: fields[currentStep].onNext,
        placeholder: fields[currentStep].placeholder
    };
};

export default useData;
